import React, { useEffect, useState, useCallback } from "react";

import apiClient from "../../../services/apiClient";
import { CONTAINER_MAX_WIDTH } from "../../../styles/layout";

// 관리자 > 협력학원 관리. 랜딩의 학원찾기 지도에 올릴 학원과 그 교사 계정을 잇는다.
//
// **계정을 이름으로 자동 확정하지 않는다.** 19곳을 이름으로 맞춰 보니 셋이 틀렸다
// (백담, 지인, 블루밍). 김해 블루밍과 대구 블루밍처럼 지역만 다른 동명 학원이 있어서
// 잘못 이으면 남의 학원 정보가 그 학원 이름으로 지도에 걸린다. 그래서 후보만 띄우고
// 고르는 것은 사람이 한다. 신청자 이름이 같은 후보에 ★ 를 붙여 준다.

const PartnerAcademyPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);
  const isMobileW = Math.min(window.innerWidth, window.screen.width) <= 768;

  const load = useCallback(() => {
    apiClient
      .get("/api/v1/echo/admin/partner-academies", { withAuth: true })
      .then((response) => setData(response.data))
      .catch(() => setError("협력학원 목록을 불러오지 못했습니다."));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleLink = async (academy, nickname) => {
    setSavingId(academy.id);
    try {
      await apiClient.post(
        "/api/v1/echo/admin/partner-academies",
        { academy_id: academy.id, nickname },
        { withAuth: true }
      );
      load();
    } catch (caught) {
      alert(caught.response?.data?.error || "저장하지 못했습니다.");
    } finally {
      setSavingId(null);
    }
  };

  const handleRemove = async (academy) => {
    const label = academy.name || "(이름 없음)";
    if (!window.confirm(`'${label}' 을 협력학원에서 뺄까요?\n\n지도에서 사라집니다. 교사 계정과 학생, 응시 기록은 그대로 남습니다.`)) return;
    setSavingId(academy.id);
    try {
      await apiClient.delete("/api/v1/echo/admin/partner-academies", {
        data: { academy_id: academy.id },
        withAuth: true,
      });
      load();
    } catch (caught) {
      alert(caught.response?.data?.error || "빼지 못했습니다.");
    } finally {
      setSavingId(null);
    }
  };

  const handleAdd = async (nickname) => {
    try {
      await apiClient.post(
        "/api/v1/echo/admin/partner-academies",
        { nickname },
        { withAuth: true }
      );
      load();
    } catch (caught) {
      alert(caught.response?.data?.error || "추가하지 못했습니다.");
    }
  };

  if (error) return <p style={{ padding: "26px" }}>{error}</p>;
  if (!data) return null;

  return (
    <div
      style={{
        padding: isMobileW ? "10px 14px 32px" : "26px 22px 44px",
        maxWidth: `${CONTAINER_MAX_WIDTH}px`,
        margin: "0 auto",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: "12px", marginBottom: "6px" }}>
        <h3 className="section-title" style={{ margin: 0 }}>협력학원 관리</h3>
        <span style={{ fontSize: "13px", color: "#7A8490" }}>
          {data.total}곳 · 계정 이어짐 {data.linked}곳 · <b style={{ color: "#00A396" }}>지도 노출 {data.on_map}곳</b>
        </span>
      </div>
      <p style={{ fontSize: "13px", color: "#7A8490", lineHeight: 1.7, marginBottom: "18px" }}>
        지도에는 <b>공개</b>이고 이어진 계정의 요금제가 <b>{data.map_plan_list.join(", ")}</b> 인 학원만 올라갑니다.
        요금제가 내려가면 저절로 빠집니다. 레벨테스트 계약 여부는 보지 않습니다.
      </p>

      <AddAcademy onAdd={handleAdd} />

      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {data.academies.map((academy) => (
          <div
            key={academy.id}
            style={{
              border: "1px solid #E6EDEC",
              borderRadius: "14px",
              background: "#FFFFFF",
              padding: isMobileW ? "13px 14px" : "16px 18px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <b style={{ fontSize: "15px" }}>{academy.name || "(이름 없음)"}</b>
              <span style={{ fontSize: "12.5px", color: "#7A8490" }}>
                {[academy.region, academy.district].filter(Boolean).join(" ")}
                {academy.applicant_name ? ` · 신청자 ${academy.applicant_name}` : ""}
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "12px",
                  fontWeight: 800,
                  borderRadius: "999px",
                  padding: "3px 11px",
                  backgroundColor: academy.on_map ? "#E4F4F1" : "#F1F4F4",
                  color: academy.on_map ? "#00786F" : "#8A939E",
                }}
              >
                {academy.on_map ? "지도 노출" : "지도 안 뜸"}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(academy)}
                disabled={savingId === academy.id}
                title="협력학원에서 뺍니다"
                style={{
                  border: "1px solid #F0DADA", background: "#FFFFFF", borderRadius: "999px",
                  padding: "3px 11px", fontSize: "12px", fontFamily: "inherit",
                  color: "#C2452D", cursor: "pointer",
                }}
              >
                빼기
              </button>
            </div>

            {academy.address && (
              <div style={{ fontSize: "12.5px", color: "#8A939E", marginTop: "4px" }}>
                {academy.address}
              </div>
            )}

            {academy.linked ? (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "13.5px" }}>
                  이어진 계정 <b>{academy.nickname}</b>
                  <span style={{ color: "#7A8490" }}> · {academy.plan || "요금제 없음"}</span>
                </span>
                <button
                  type="button"
                  onClick={() => handleLink(academy, "")}
                  disabled={savingId === academy.id}
                  style={{
                    border: "1px solid #E6EDEC", background: "#FFFFFF", borderRadius: "999px",
                    padding: "5px 13px", fontSize: "12.5px", fontFamily: "inherit",
                    color: "#7A8490", cursor: "pointer",
                  }}
                >
                  끊기
                </button>
              </div>
            ) : (
              <div style={{ marginTop: "10px" }}>
                <div style={{ fontSize: "12.5px", color: "#7A8490", marginBottom: "6px" }}>
                  이을 계정을 고르세요. ★ 는 신청자 이름이 같은 계정입니다.
                </div>
                {academy.candidates.length === 0 && (
                  <div style={{ fontSize: "13px", color: "#C2452D" }}>
                    후보를 찾지 못했습니다. 계정 아이디를 직접 넣어 주세요.
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {academy.candidates.map((one) => (
                    <button
                      key={one.nickname}
                      type="button"
                      onClick={() => handleLink(academy, one.nickname)}
                      disabled={savingId === academy.id}
                      style={{
                        display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap",
                        textAlign: "left", width: "100%",
                        border: `1px solid ${one.applicant_match ? "#00A396" : "#E6EDEC"}`,
                        background: one.applicant_match ? "#F5FBFA" : "#FFFFFF",
                        borderRadius: "10px", padding: "9px 12px",
                        fontFamily: "inherit", fontSize: "13px", cursor: "pointer",
                      }}
                    >
                      {one.applicant_match && <span style={{ color: "#00A396", fontWeight: 800 }}>★</span>}
                      <b>{one.nickname}</b>
                      <span>{one.name}</span>
                      <span style={{ color: "#7A8490" }}>{one.organization_name || "(학원명 없음)"}</span>
                      <span style={{ color: one.on_map ? "#00786F" : "#C2452D", fontWeight: 700 }}>
                        {one.plan || "요금제 없음"}
                      </span>
                      <span style={{ marginLeft: "auto", color: "#8A939E", fontVariantNumeric: "tabular-nums" }}>
                        문제지 {one.worksheet_count}개
                      </span>
                    </button>
                  ))}
                </div>
                <DirectLink academy={academy} onLink={handleLink} disabled={savingId === academy.id} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// 새 협력학원 추가. 계정 아이디만 받고 학원명은 그 계정에서 가져온다.
const AddAcademy = ({ onAdd }) => {
  const [value, setValue] = useState("");
  return (
    <div
      style={{
        display: "flex", gap: "6px", alignItems: "center",
        border: "1px dashed #CBD8D6", borderRadius: "12px",
        padding: "11px 14px", marginBottom: "14px", background: "#FAFCFC",
      }}
    >
      <span style={{ fontSize: "13px", fontWeight: 700, flexShrink: 0 }}>협력학원 추가</span>
      <input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="교사 계정 아이디 (예: ipadchego)"
        style={{
          flex: 1, minWidth: 0, border: "1px solid #E6EDEC", borderRadius: "9px",
          padding: "7px 11px", fontFamily: "inherit", fontSize: "13px",
        }}
      />
      <button
        type="button"
        onClick={() => { if (value.trim()) { onAdd(value.trim()); setValue(""); } }}
        disabled={!value.trim()}
        style={{
          border: "none", background: "#00A396", color: "#FFF", borderRadius: "9px",
          padding: "7px 16px", fontFamily: "inherit", fontSize: "13px", fontWeight: 700,
          cursor: value.trim() ? "pointer" : "default",
        }}
      >
        추가
      </button>
    </div>
  );
};

// 후보에 없는 계정을 직접 넣는 자리. 이름이 전혀 다른 학원이 있어서 필요하다.
const DirectLink = ({ academy, onLink, disabled }) => {
  const [value, setValue] = useState("");
  return (
    <div style={{ display: "flex", gap: "6px", marginTop: "8px" }}>
      <input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="계정 아이디 직접 입력"
        style={{
          flex: 1, minWidth: 0, border: "1px solid #E6EDEC", borderRadius: "9px",
          padding: "7px 11px", fontFamily: "inherit", fontSize: "13px",
        }}
      />
      <button
        type="button"
        onClick={() => value.trim() && onLink(academy, value.trim())}
        disabled={disabled || !value.trim()}
        style={{
          border: "none", background: "#0E141B", color: "#FFF", borderRadius: "9px",
          padding: "7px 15px", fontFamily: "inherit", fontSize: "13px", fontWeight: 700,
          cursor: value.trim() ? "pointer" : "default",
        }}
      >
        잇기
      </button>
    </div>
  );
};

export default PartnerAcademyPage;
